// Hook: Subtask mutations (toggle completion, update value)
// The backend type is SubtaskType / field is `subtasks`; the mutation is updateChecklistItem.
// Run `yarn codegen` after adding mutations.federation.gql to generate typed hooks.

import { useUpdateChecklistItemMutation, GetWorkflowDocument } from "#graphql/supergraphHooks";
import { onApiError } from "../utils/apiErrorToast";
import { isSubtaskValueComplete } from "../utils/subtaskCompletion";

interface UseSubtaskActionsResult {
  toggleSubtask: (subtaskId: number, completed: boolean) => Promise<void>;
  updateSubtaskValue: (
    subtaskId: number,
    value: string | boolean,
    currentlyCompleted: boolean,
  ) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

export function useSubtaskActions(workflowId?: string): UseSubtaskActionsResult {
  // Guard against non-numeric workflowId — Number("abc") would refetch with id: NaN.
  const parsedId = workflowId ? Number(workflowId) : NaN;
  const refetchOptions = {
    ...(Number.isFinite(parsedId)
      ? {
          refetchQueries: [
            {
              query: GetWorkflowDocument,
              variables: { id: parsedId },
            },
          ],
        }
      : {}),
    onError: onApiError,
  };

  const [updateChecklistItem, { loading, error }] =
    useUpdateChecklistItemMutation(refetchOptions);

  const toggleSubtask = async (subtaskId: number, completed: boolean) => {
    await updateChecklistItem({
      variables: {
        subtaskId,
        isCompleted: completed,
      },
    });
  };

  // BE updateChecklistItem requires isCompleted on every call.
  // Completion is derived from whether a non-empty value is present: a filled
  // radio button or input field IS the completion signal for those subtask types.
  // Clearing the value (empty string / false) marks the item incomplete again.
  // The legacy `currentlyCompleted` parameter is retained for call-site
  // compatibility but is no longer used to avoid radio/input items permanently
  // remaining incomplete because they were never explicitly toggled.
  const updateSubtaskValue = async (
    subtaskId: number,
    value: string | boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _currentlyCompleted: boolean,
  ) => {
    await updateChecklistItem({
      variables: {
        subtaskId,
        isCompleted: isSubtaskValueComplete(value),
        value: typeof value === "boolean" ? String(value) : value,
      },
    });
  };

  return {
    toggleSubtask,
    updateSubtaskValue,
    loading,
    error: error ?? null,
  };
}
